import React, { useEffect, useMemo, useRef, useState } from "react";

// A select that can be searched: the closed field shows the chosen option, typing
// filters the list. Used for the organizer's own question on the buy page, where
// the option list can be long.
const SearchableSelect = ({
  options = [],
  value = "",
  onChange,
  placeholder = "Seçin",
  searchPlaceholder = "Axtarın...",
  emptyText = "Uyğun seçim tapılmadı",
  id,
  required,
  inputClassName = "",
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("az");
    if (!needle) return options;
    return options.filter((option) => option.toLocaleLowerCase("az").includes(needle));
  }, [options, query]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIndex(Math.max(0, options.indexOf(value)));
      searchRef.current?.focus();
    }
  }, [isOpen, options, value]);

  useEffect(() => {
    if (!isOpen) return;
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, isOpen]);

  const choose = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!filtered.length) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => (current + step + filtered.length) % filtered.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (filtered[activeIndex]) choose(filtered[activeIndex]);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`${inputClassName} flex items-center justify-between gap-2 text-left disabled:opacity-60`}
      >
        <span className={value ? "text-surface-900 truncate" : "text-surface-400 truncate"}>
          {value || placeholder}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {value && !required && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Seçimi ləğv et"
              onClick={(event) => {
                event.stopPropagation();
                onChange("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.stopPropagation();
                  onChange("");
                }
              }}
              className="text-surface-400 hover:text-surface-600 text-xs px-1"
            >
              ✕
            </span>
          )}
          <svg className={`w-4 h-4 text-surface-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-30 mt-2 w-full bg-white rounded-2xl shadow-xl border border-surface-100 overflow-hidden">
          <div className="p-2 border-b border-surface-100">
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="w-full border-0 bg-surface-50 rounded-xl px-3 py-2 text-sm text-surface-900 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1" role="listbox" ref={listRef}>
            {filtered.map((option, index) => (
              <li key={option}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option === value}
                  data-active={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(option)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                    option === value
                      ? "bg-accent-50 text-accent-700 font-semibold"
                      : index === activeIndex
                      ? "bg-surface-50 text-surface-900"
                      : "text-surface-700"
                  }`}
                >
                  {option}
                </button>
              </li>
            ))}
            {!filtered.length && (
              <li className="px-4 py-3 text-sm text-surface-400">{emptyText}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
